import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';

import ControlledDraftJson from '../components/ControlledDraftJson';

const API_BASE = process.env.REACT_APP_API_URL;

const STATUS_COLORS = {
  Draft: '#94a3b8',
  'In Review': '#60a5fa',
  Released: '#46BB89',
  Superseded: '#f59e0b',
  Obsolete: '#EC2124',
};

function StatusBadge({ status }) {
  const color = STATUS_COLORS[status] || '#666';
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
      background: color + '22', color,
    }}>{status}</span>
  );
}

function normalizeRole(role) {
  if (!role) return '';
  if (Array.isArray(role)) return (role[0]||'').toLowerCase().trim();
  return String(role).toLowerCase().trim();
}

export default function ProcessFlowList() {
  const { getToken } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();

  const role = normalizeRole(user?.publicMetadata?.role) || normalizeRole(user?.publicMetadata?.roles);
  const canEdit = ['admin', 'quality-manager', 'production-engineer', 'service'].includes(role) || ['gpv','ubiqconn','acmepoint','hk_ringer'].includes(String(role||'').replace(/^test-/,''));
  // Catalog imports are NI-staff-only on the backend (canManage). Gate the import
  // buttons to staff so suppliers don't see an action that always 403s.
  const canManageCatalog = ['admin', 'quality-manager', 'production-engineer', 'service'].includes(role);

  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: 'Released', productFamily: '', supplier: '' });
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.productFamily) params.set('productFamily', filters.productFamily);
      if (filters.supplier) params.set('supplier', filters.supplier);
      const res = await window.fetch(`${API_BASE}/quality/process-flows?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.ok) setFlows(data.flows || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [getToken, filters]);

  useEffect(() => { fetch(); }, [fetch]);

  // Re-runs an idempotent QMS catalog import (process flows, tests, jigs,
  // equipment, docs, notes upsert in place from the seed). Shared by the
  // SharePoint catalog and the Wave XL SOP seed.
  const runImport = async (path, label) => {
    setSyncing(true); setSyncMsg('');
    try {
      const token = await getToken();
      const res = await window.fetch(`${API_BASE}${path}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.message || data.error || `Import failed (${res.status})`);
      const s = data.summary || {};
      const fmt = (k) => `${(s[k]?.created || 0) + (s[k]?.updated || 0)}`;
      const bl = s.bomLink;
      const bomMsg = !bl ? ''
        : bl.status === 'linked' ? ` · BOM linked: ${bl.documentNumber}`
        : bl.status === 'ambiguous' ? ` · BOM not auto-linked (${(bl.candidates || []).length} matches — attach manually)`
        : ' · BOM not found (attach manually)';
      setSyncMsg(`${label} synced — flows ${fmt('processFlows')}, docs ${fmt('qualityDocuments')}, tests ${fmt('tests')}, notes ${fmt('notes')}, jigs ${fmt('jigs')}, equipment ${fmt('equipment')}, work orders ${fmt('workOrders')}.${bomMsg}`);
      fetch();
    } catch (e) {
      setSyncMsg(`Sync failed: ${e.message}`);
    } finally { setSyncing(false); }
  };

  const setFilter = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  const fs = {
    background: 'var(--ni-panel)', border: '1px solid var(--ni-border-soft)',
    color: 'var(--ni-text)', borderRadius: 6, padding: '6px 10px', fontSize: 13,
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>Process Flows</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => navigate('/operate')}
            title="Open a workstation: pick a line and station, then operate it (fullscreen kiosk)"
            style={{ background: 'rgba(70,187,137,0.12)', color: 'var(--ni-green)', border: '1px solid rgba(70,187,137,0.35)',
              borderRadius: 6, padding: '8px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
          >
            ▶ Operate a station
          </button>
        {canManageCatalog && (
          <>
            <button
              onClick={() => runImport('/quality/import/sharepoint', 'SharePoint catalog')} disabled={syncing}
              title="Upsert the SharePoint QMS catalog (process flows, documents, tests, jigs, equipment, notes, work orders)"
              style={{ background: 'rgba(96,165,250,0.12)', color: '#60a5fa', border: '1px solid rgba(96,165,250,0.3)',
                borderRadius: 6, padding: '8px 14px', fontWeight: 700, fontSize: 13,
                cursor: syncing ? 'wait' : 'pointer', opacity: syncing ? 0.6 : 1 }}
            >
              {syncing ? 'Syncing…' : '⟳ Sync catalog'}
            </button>
            <button
              onClick={() => runImport('/quality/import/wave-xl', 'Wave XL SOP')} disabled={syncing}
              title="Import the Wave XL — Production SOP (working draft): flow PF-WXL-001, equipment, jigs, tests, records and open-item notes"
              style={{ background: 'rgba(245,158,11,0.12)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.3)',
                borderRadius: 6, padding: '8px 14px', fontWeight: 700, fontSize: 13,
                cursor: syncing ? 'wait' : 'pointer', opacity: syncing ? 0.6 : 1 }}
            >
              {syncing ? 'Importing…' : '⬇ Import Wave XL SOP'}
            </button>
          </>
        )}
        {canEdit && (
          <button
            onClick={() => navigate('/quality/process/new')}
            style={{ background: 'var(--ni-green)', color: '#000', border: 'none', borderRadius: 6,
              padding: '8px 18px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
          >
            + New Flow
          </button>
        )}
        </div>
      </div>

      <ControlledDraftJson library="process-flows" onApplied={fetch} />

      {syncMsg && (
        <div style={{ marginBottom: 14, padding: '8px 12px', borderRadius: 6, fontSize: 12,
          background: syncMsg.startsWith('Sync failed') ? 'rgba(236,33,36,0.1)' : 'rgba(70,187,137,0.1)',
          border: `1px solid ${syncMsg.startsWith('Sync failed') ? 'rgba(236,33,36,0.3)' : 'rgba(70,187,137,0.3)'}`,
          color: syncMsg.startsWith('Sync failed') ? '#EC2124' : 'var(--ni-green)' }}>
          {syncMsg}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {canEdit && (
          <select value={filters.status} onChange={e => setFilter('status', e.target.value)} style={fs}>
            <option value="">All statuses</option>
            {['Draft','In Review','Released','Superseded','Obsolete'].map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        )}
        <input
          placeholder="Product family…"
          value={filters.productFamily}
          onChange={e => setFilter('productFamily', e.target.value)}
          style={{ ...fs, minWidth: 160 }}
        />
        <input
          placeholder="Supplier…"
          value={filters.supplier}
          onChange={e => setFilter('supplier', e.target.value)}
          style={{ ...fs, minWidth: 140 }}
        />
      </div>

      {/* Table */}
      <div style={{ background: 'var(--ni-panel)', border: '1px solid var(--ni-border-soft)', borderRadius: 10, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--ni-text-muted)' }}>Loading…</div>
        ) : flows.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--ni-text-muted)' }}>
            No process flows found.{' '}
            {canEdit && (
              <button onClick={() => navigate('/quality/process/new')}
                style={{ background: 'none', border: 'none', color: 'var(--ni-green)', cursor: 'pointer', fontSize: 14 }}>
                Create one.
              </button>
            )}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ color: 'var(--ni-text-muted)', fontSize: 11, textTransform: 'uppercase',
                  letterSpacing: '0.06em', background: 'rgba(0,0,0,0.2)' }}>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' }}>Doc No.</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600 }}>Name</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' }}>Product Family</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600 }}>Suppliers</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600 }}>Rev.</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600 }}>Status</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' }}>Updated</th>
                </tr>
              </thead>
              <tbody>
                {flows.map(flow => (
                  <tr key={flow._id}
                    onClick={() => navigate(`/quality/process/${flow._id}`)}
                    style={{ cursor: 'pointer', borderTop: '1px solid var(--ni-border-soft)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '11px 16px', fontFamily: 'monospace', fontSize: 12, color: '#60a5fa', whiteSpace: 'nowrap' }}>{flow.documentNumber}</td>
                    <td style={{ padding: '11px 16px', fontWeight: 600 }}>{flow.name}</td>
                    <td style={{ padding: '11px 16px', color: 'var(--ni-text-muted)' }}>{flow.productFamily || '—'}</td>
                    <td style={{ padding: '11px 16px', color: 'var(--ni-text-muted)', fontSize: 12 }}>
                      {(flow.suppliers || []).join(', ') || '—'}
                    </td>
                    <td style={{ padding: '11px 16px', color: 'var(--ni-text-muted)', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{flow.currentRevision || '—'}</td>
                    <td style={{ padding: '11px 16px', whiteSpace: 'nowrap' }}><StatusBadge status={flow.currentStatus} /></td>
                    <td style={{ padding: '11px 16px', color: 'var(--ni-text-muted)', whiteSpace: 'nowrap' }}>
                      {flow.updatedAt ? new Date(flow.updatedAt).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div style={{ marginTop: 10, color: 'var(--ni-text-muted)', fontSize: 12 }}>{flows.length} flow{flows.length !== 1 ? 's' : ''}</div>
    </div>
  );
}
